import { Container, Sprite, Texture } from 'pixi.js';

import type { GameAssets } from '../../game/assets/GameAssets';
import { WORLD_TILE_HEIGHT, WORLD_TILE_WIDTH } from '../../game/config';
import { terrainConfig } from '../../game/terrain/TerrainRenderConfig';
import type { SeasonState } from '../seasons/SeasonTypes';
import type { MapData, MapTile } from '../world/types';
import { TerrainTextureResolver } from './TerrainTextureResolver';

const TILE_OVERDRAW = terrainConfig.overlaySizing.defaultOverdraw;

interface TileView {
  readonly base: Sprite;
  readonly overlay: Sprite;
}

export class TilemapRenderer {
  private readonly tileSprites = new Map<string, TileView>();
  private readonly textureResolver: TerrainTextureResolver;
  private mapLookup = new Map<string, MapTile>();
  private season?: SeasonState;

  constructor(
    private readonly layer: Container,
    private readonly assets: GameAssets,
    private readonly options: { visualPolish?: boolean; waterMode?: 'tiles' } = {},
  ) {
    this.textureResolver = new TerrainTextureResolver(assets);
  }

  render(map: MapData, season?: SeasonState): void {
    const seen = new Set<string>();
    this.season = season;
    this.mapLookup = new Map(map.tiles.map((tile) => [`${tile.x}:${tile.y}`, tile]));
    for (const tile of map.tiles) {
      const key = `${tile.x}:${tile.y}`;
      seen.add(key);
      const view = this.tileSprites.get(key) ?? this.createTileView(key);
      this.drawTile(view, tile);
    }

    for (const [key, view] of this.tileSprites) {
      if (!seen.has(key)) {
        view.base.destroy();
        view.overlay.destroy();
        this.tileSprites.delete(key);
      }
    }
  }

  private createTileView(key: string): TileView {
    const base = new Sprite(Texture.EMPTY);
    const overlay = new Sprite(Texture.EMPTY);
    base.anchor.set(0.5, 0.5);
    overlay.anchor.set(0.5, 0.5);
    base.eventMode = 'none';
    overlay.eventMode = 'none';
    this.tileSprites.set(key, { base, overlay });
    this.layer.addChild(base, overlay);
    return { base, overlay };
  }

  private drawTile(view: TileView, tile: MapTile): void {
    view.base.texture = this.textureResolver.ground(tile, this.resolverContext());
    const tileCenterX = Math.round(tile.x * WORLD_TILE_WIDTH + WORLD_TILE_WIDTH / 2);
    const tileCenterY = Math.round(tile.y * WORLD_TILE_HEIGHT + WORLD_TILE_HEIGHT / 2);
    view.base.position.set(tileCenterX, tileCenterY);
    view.base.rotation = 0;
    view.base.width = WORLD_TILE_WIDTH + TILE_OVERDRAW;
    view.base.height = WORLD_TILE_HEIGHT + TILE_OVERDRAW;

    const overlay = this.textureResolver.overlay(tile, this.resolverContext());
    view.overlay.texture = overlay ?? Texture.EMPTY;
    view.overlay.visible = Boolean(overlay);
    view.overlay.position.set(tileCenterX, tileCenterY);
    view.overlay.rotation = this.resolveOverlayRotation(tile);
    this.sizeOverlay(view.overlay, tile);
  }

  private resolveOverlayRotation(tile: MapTile): number {
    if (tile.type === 'road') return this.resolveRoadRotation(tile);
    if (tile.type === 'water') return this.resolveWaterRotation(tile);
    return 0;
  }

  private resolveRoadRotation(tile: MapTile): number {
    const n = this.isRoad(tile.x, tile.y - 1);
    const s = this.isRoad(tile.x, tile.y + 1);
    const e = this.isRoad(tile.x + 1, tile.y);
    const w = this.isRoad(tile.x - 1, tile.y);
    const count = Number(n) + Number(s) + Number(e) + Number(w);

    if (count === 1) {
      if (e) return Math.PI / 2;
      if (s) return Math.PI;
      if (w) return -Math.PI / 2;
      return 0;
    }

    if (count === 2 && !((n && s) || (e && w))) {
      if (s && e) return 0;
      if (s && w) return Math.PI / 2;
      if (n && w) return Math.PI;
      return -Math.PI / 2;
    }

    if (count === 3) {
      if (!n) return Math.PI;
      if (!e) return Math.PI / 2;
      if (!s) return 0;
      return -Math.PI / 2;
    }

    if (e && w && !(n || s)) return 0;
    return 0;
  }

  private resolveWaterRotation(tile: MapTile): number {
    if (!this.options.visualPolish && tile.variant && this.assets.reference[tile.variant]) return 0;
    const variant = tile.variant ?? '';

    if (
      this.options.visualPolish &&
      (variant.startsWith('water27/river_') ||
        variant.startsWith('water27/bridge_') ||
        variant.startsWith('water27/sea_deep') ||
        variant.startsWith('water27/shallow') ||
        variant.startsWith('water27/shore_'))
    ) {
      return 0;
    }

    const n = this.isWater(tile.x, tile.y - 1);
    const s = this.isWater(tile.x, tile.y + 1);
    const e = this.isWater(tile.x + 1, tile.y);
    const w = this.isWater(tile.x - 1, tile.y);

    if (variant.includes('river_bend_ne') || variant.includes('shore_in_ne') || variant.includes('shore_out_ne')) return -Math.PI / 2;
    if (variant.includes('river_bend_nw') || variant.includes('shore_in_nw') || variant.includes('shore_out_nw')) return Math.PI;
    if (variant.includes('river_bend_sw') || variant.includes('shore_in_sw') || variant.includes('shore_out_sw')) return Math.PI / 2;
    if (variant.includes('river_bend_se') || variant.includes('shore_in_se') || variant.includes('shore_out_se')) return 0;

    if (variant.includes('river_t_n')) return Math.PI;
    if (variant.includes('river_t_e')) return Math.PI / 2;
    if (variant.includes('river_t_s')) return 0;
    if (variant.includes('river_t_w')) return -Math.PI / 2;
    if (variant.includes('river_cross')) return 0;

    if (variant.includes('shore_east')) return Math.PI / 2;
    if (variant.includes('shore_south')) return Math.PI;
    if (variant.includes('shore_west')) return -Math.PI / 2;
    if (variant.includes('shore_north')) return 0;

    if ((n || s) && (e || w)) {
      if (s && e) return 0;
      if (s && w) return Math.PI / 2;
      if (n && w) return Math.PI;
      return -Math.PI / 2;
    }

    if (tile.variant?.includes('n_e')) return -Math.PI / 2;
    if (tile.variant?.includes('n_w')) return Math.PI;
    if (tile.variant?.includes('s_w')) return Math.PI / 2;

    return 0;
  }

  private isRoad(x: number, y: number): boolean {
    const tile = this.mapLookup.get(`${x}:${y}`);
    return tile?.type === 'road' || tile?.variant === 'bridge_h' || tile?.variant === 'bridge_v';
  }

  private isWater(x: number, y: number): boolean {
    const type = this.mapLookup.get(`${x}:${y}`)?.type;
    return type === 'water' || type === 'shore';
  }

  private sizeOverlay(sprite: Sprite, tile: MapTile): void {
    if (tile.type === 'road') {
      sprite.width = WORLD_TILE_WIDTH + terrainConfig.overlaySizing.roadOverdraw;
      sprite.height = WORLD_TILE_HEIGHT + terrainConfig.overlaySizing.roadOverdraw;
      return;
    }

    if (this.options.visualPolish && tile.type === 'water') {
      if (tile.variant === 'bridge_h' || tile.variant === 'bridge_v') {
        sprite.width = WORLD_TILE_WIDTH + terrainConfig.overlaySizing.visualPolishWaterOverdraw;
        sprite.height = WORLD_TILE_HEIGHT + terrainConfig.overlaySizing.visualPolishWaterOverdraw;
        return;
      }

      if (tile.variant?.startsWith('water27/river_')) {
        sprite.width = WORLD_TILE_WIDTH + 18;
        sprite.height = WORLD_TILE_HEIGHT + 12;
        return;
      }

      sprite.width = WORLD_TILE_WIDTH + terrainConfig.overlaySizing.visualPolishWaterOverdraw;
      sprite.height = WORLD_TILE_HEIGHT + terrainConfig.overlaySizing.visualPolishWaterOverdraw;
      return;
    }

    sprite.width = WORLD_TILE_WIDTH + TILE_OVERDRAW;
    sprite.height = WORLD_TILE_HEIGHT + TILE_OVERDRAW;
  }

  private resolverContext(): {
    readonly season?: SeasonState;
    readonly visualPolish?: boolean;
    readonly waterMode?: 'tiles';
    readonly isRoad: (x: number, y: number) => boolean;
    readonly isWater: (x: number, y: number) => boolean;
  } {
    return {
      season: this.season,
      visualPolish: this.options.visualPolish,
      waterMode: this.options.waterMode,
      isRoad: (x, y) => this.isRoad(x, y),
      isWater: (x, y) => this.isWater(x, y),
    };
  }
}
